import { State } from 'ts-fsrs';
import type { ChooseCorrect, TranslateTyped } from '@fluent/types';
import { createRng } from '../lesson/shuffle.js';
import { normalizeAnswer } from '../lesson/matchAnswer.js';
import type { DeckEntry } from './deck.js';
import type { SessionCard } from './sessionReducer.js';

/**
 * Question building for test-mode flashcard sessions (DECISIONS.md
 * 2026-07-16 "Flashcards become tests"). Every non-teach card appearance is
 * a question; the format follows the card's live FSRS state:
 *
 * - YOUNG (Learning / Relearning / young Review) → multiple choice.
 *   Recognition — one tap, gentle. A card failed minutes ago always comes
 *   back as choice, never punishing typed after a lapse.
 * - MATURE (Review with a week-plus interval) → typed. Production — the
 *   learner must produce the English, with the lessons' forgiving matcher.
 */

export type Question =
  | { kind: 'choice'; exercise: ChooseCorrect }
  | { kind: 'typed'; exercise: TranslateTyped };

/** One distractor-pool entry: English display text + every normalized
 *  Georgian it translates (to exclude same-ka synonyms that would make a
 *  second correct option). `kas` collects ALL Georgian texts of entries
 *  that deduped into this one — a dropped variant's ka must still count. */
export interface PoolEntry {
  en: string;
  kas: string[];
}

/** Max options on a choice question (including the answer). */
const CHOICE_OPTIONS = 4;

/**
 * Build the distractor pool from the learner's full deck. Dedupes by
 * normalized English (so "Hello!" and "hello" can't render as twin
 * options); keeps every ka text for the synonym filter at question time.
 */
export function buildPool(entries: readonly DeckEntry[]): PoolEntry[] {
  const byEn = new Map<string, PoolEntry>();
  for (const e of entries) {
    const norm = normalizeAnswer(e.en);
    if (norm.length === 0) continue;
    const kaNorm = normalizeAnswer(e.ka);
    const existing = byEn.get(norm);
    if (existing) {
      if (!existing.kas.includes(kaNorm)) existing.kas.push(kaNorm);
    } else {
      byEn.set(norm, { en: e.en, kas: [kaNorm] });
    }
  }
  return [...byEn.values()];
}

/** Typed production is demanded only once FSRS says the memory is
 *  established: a real Review-state card scheduled a week or more out.
 *  Uses the LIVE in-session card (a just-failed card is Relearning →
 *  choice on its replay), not the stale DB row. */
export function isMature(card: SessionCard): boolean {
  return card.card.state === State.Review && card.card.scheduled_days >= 7;
}

/**
 * The silhouette of an English answer: how many words it has, and whether it
 * reads as a sentence (ends in . ! ? or holds a comma). Used to keep a
 * multiple-choice question's options looking alike.
 */
export function shapeOf(en: string): { words: number; sentence: boolean } {
  const trimmed = en.trim();
  return {
    words: trimmed.split(/\s+/).filter(Boolean).length,
    sentence: /[.!?]$/.test(trimmed) || trimmed.includes(','),
  };
}

/**
 * Build the question for a card appearance.
 *
 * `seed` must be unique per (session, card, appearance) so option layouts
 * vary across sessions and replays but stay stable across re-renders of
 * the same appearance.
 *
 * Falls back to typed when the pool can't supply a single distractor
 * (degenerate one-word decks) — the choice schema needs ≥2 options.
 */
export function buildQuestion(card: SessionCard, pool: readonly PoolEntry[], seed: string): Question {
  if (isMature(card)) return typedQuestion(card, pool, seed);

  const answerNorm = normalizeAnswer(card.en);
  const kaNorm = normalizeAnswer(card.ka);
  const candidates = pool.filter(
    (p) => normalizeAnswer(p.en) !== answerNorm && !p.kas.includes(kaNorm),
  );
  if (candidates.length === 0) return typedQuestion(card, pool, seed);

  // Seeded draw without replacement, then seeded position for the answer.
  const rng = createRng(seed);
  const shuffled = [...candidates];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  // Prefer distractors SHAPED like the answer. A one-word answer beside three
  // full sentences is answerable without reading any of them — "hello" among
  // "have", "I am fine, thanks.", "goodbye" gives itself away by silhouette
  // (audit F113). Tier 1 is the same shape and near the same length, tier 2
  // the same shape, tier 3 whatever is left, so a thin deck still fills up.
  const answerShape = shapeOf(card.en);
  const tier = (p: PoolEntry): number => {
    const s = shapeOf(p.en);
    if (s.sentence !== answerShape.sentence) return 2;
    return Math.abs(s.words - answerShape.words) <= 1 ? 0 : 1;
  };
  shuffled.sort((a, b) => tier(a) - tier(b)); // stable: keeps the seeded order inside a tier
  const options = shuffled.slice(0, CHOICE_OPTIONS - 1).map((p) => p.en);
  const at = Math.floor(rng() * (options.length + 1));
  options.splice(at, 0, card.en);

  return {
    kind: 'choice',
    exercise: {
      type: 'choose_correct',
      id: `fcq-${seed}`,
      prompt: card.ka,
      answer: card.en,
      options,
    },
  };
}

function typedQuestion(card: SessionCard, pool: readonly PoolEntry[], seed: string): Question {
  // Same-ka synonyms from the learner's own deck are correct answers too —
  // typing "hi" for „გამარჯობა" when the card is "hello" must not lapse a
  // mature card. matchAnswer accepts them via alt_answers.
  const answerNorm = normalizeAnswer(card.en);
  const kaNorm = normalizeAnswer(card.ka);
  const alt = pool
    .filter((p) => p.kas.includes(kaNorm) && normalizeAnswer(p.en) !== answerNorm)
    .map((p) => p.en);
  return {
    kind: 'typed',
    exercise: {
      type: 'translate_typed',
      id: `fcq-${seed}`,
      prompt: card.ka,
      answer: card.en,
      alt_answers: alt,
    },
  };
}
